import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Eye, Check, X, Mail, Search, Loader2, Trash2, Pencil, Ban, Save, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import OrderItemDetail from "@/components/OrderItemDetail";

const statusColors = {
  draft: "bg-slate-100 text-slate-800 border-slate-200",
  pending_approval: "bg-blue-100 text-blue-800 border-blue-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200"
};

const statusLabels = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
  converted: "Converted to Order"
};

export default function QuotationsPage() {
  const { isCustomer, hasRole } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [freightCost, setFreightCost] = useState(0);
  const [adminNotes, setAdminNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const perPage = 50;

  // Admin alter state
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // Admin reject state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const isSalesAdmin = hasRole("super_admin", "sales_manager");

  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "created_at" ? "desc" : "asc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronsUpDown size={12} className="ml-1 opacity-40 inline-block" />;
    return sortDir === "asc" ? <ChevronUp size={12} className="ml-1 inline-block" /> : <ChevronDown size={12} className="ml-1 inline-block" />;
  };

  const sortedQuotations = useMemo(() => {
    return [...quotations].sort((a, b) => {
      let av = a[sortField] ?? "", bv = b[sortField] ?? "";
      if (sortField === "total" || sortField === "items") {
        av = sortField === "items" ? (a.items?.length ?? 0) : (a.total ?? 0);
        bv = sortField === "items" ? (b.items?.length ?? 0) : (b.total ?? 0);
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [quotations, sortField, sortDir]);

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (search) params.append("search", search);
      const res = await api.get(`/quotations?${params}`);
      setQuotations(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, search]);

  useEffect(() => { fetchQuotations(); }, [fetchQuotations]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const sendConfirmationMail = async () => {
    if (!selectedQuote) return;
    setConfirming(true);
    try {
      const payload = { freight_cost: freightCost };
      if (adminNotes.trim()) payload.admin_notes = adminNotes.trim();
      if (deliveryDate) payload.expected_delivery_date = deliveryDate;
      await api.put(`/quotations/${selectedQuote.id}/confirm`, payload);
      toast.success("Confirmation sent to customer!");
      fetchQuotations();
      setDetailOpen(false);
      setEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send confirmation");
    } finally {
      setConfirming(false);
    }
  };

  const approveQuotation = async () => {
    if (!selectedQuote) return;
    setConfirming(true);
    try {
      await api.put(`/quotations/${selectedQuote.id}/approve`);
      toast.success("Order created successfully!");
      fetchQuotations();
      setDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setConfirming(false);
    }
  };

  const rejectQuotation = async () => {
    if (!selectedQuote) return;
    setConfirming(true);
    try {
      await api.put(`/quotations/${selectedQuote.id}/reject`);
      toast.success("Quotation rejected");
      fetchQuotations();
      setDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setConfirming(false);
    }
  };

  // Admin reject with reason
  const adminRejectQuotation = async () => {
    if (!selectedQuote || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setRejecting(true);
    try {
      await api.put(`/quotations/${selectedQuote.id}/admin-reject`, {
        rejection_reason: rejectionReason.trim()
      });
      toast.success("Quotation rejected");
      fetchQuotations();
      setDetailOpen(false);
      setShowRejectDialog(false);
      setRejectionReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setRejecting(false);
    }
  };

  // Enter edit mode
  const enterEditMode = () => {
    setEditItems(
      selectedQuote.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        product_code: item.product_code,
        filename: item.filename,
        quantity: item.quantity,
        item_total: item.item_total,
        remove: false
      }))
    );
    setEditMode(true);
  };

  const updateEditQty = (index, qty) => {
    setEditItems(prev => prev.map((item, i) =>
      i === index ? { ...item, quantity: Math.max(1, parseInt(qty) || 1) } : item
    ));
  };

  const toggleRemoveItem = (index) => {
    setEditItems(prev => prev.map((item, i) =>
      i === index ? { ...item, remove: !item.remove } : item
    ));
  };

  // Save alterations
  const saveAlterations = async () => {
    const activeItems = editItems.filter(i => !i.remove);
    if (activeItems.length === 0) {
      toast.error("Cannot remove all items");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        items: editItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          remove: item.remove
        })),
        freight_cost: freightCost,
      };
      if (adminNotes.trim()) payload.admin_notes = adminNotes.trim();
      if (deliveryDate) payload.expected_delivery_date = deliveryDate;

      const res = await api.put(`/quotations/${selectedQuote.id}/alter`, payload);
      toast.success("Quotation updated successfully");
      setSelectedQuote(res.data);
      setEditMode(false);
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update quotation");
    } finally {
      setSaving(false);
    }
  };

  const viewDetail = async (quote) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setEditMode(false);
    try {
      const res = await api.get(`/quotations/${quote.id}`);
      setSelectedQuote(res.data);
      setFreightCost(res.data.freight_cost || 0);
      setAdminNotes(res.data.admin_notes || "");
      setDeliveryDate(res.data.expected_delivery_date || "");
    } catch {
      toast.error("Failed to load quotation details");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="quotations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="quotations-title">
            Pending Orders
          </h1>
          <p className="text-slate-500 mt-1">View and manage orders awaiting confirmation</p>
        </div>
        
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
          <SelectTrigger className="w-48 rounded-none" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input placeholder="Search by quote #, customer, company..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-10 rounded-none" data-testid="search-input" />
      </div>

      {/* Mobile card list */}
      {loading ? (
        <div className="flex justify-center py-12 md:hidden"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : sortedQuotations.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-slate-400 md:hidden">
          <FileText className="w-10 h-10 mb-3 opacity-20" />
          <p className="text-sm">No quotations found</p>
        </div>
      ) : (
        <div className="space-y-2 md:hidden">
          {sortedQuotations.map(quote => (
            <div key={quote.id} className="bg-white border border-slate-200 p-4 active:bg-slate-50" onClick={() => viewDetail(quote)}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="font-mono text-sm font-semibold text-slate-900">{quote.quote_number}</p>
                  {quote.order_number && <p className="font-mono text-xs text-green-700 font-semibold">{quote.order_number}</p>}
                </div>
                <Badge className={`badge-status ${statusColors[quote.status]} text-[10px] flex-shrink-0`}>{statusLabels[quote.status]}</Badge>
              </div>
              {!isCustomer() && (
                <p className="text-sm font-medium text-slate-800 mb-1">{quote.customer_name} <span className="text-xs text-slate-400 font-normal">· {quote.company_name}</span></p>
              )}
              <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                <span>{quote.items.length} item(s)</span>
                <span className="font-mono font-semibold text-slate-800">₹{quote.total.toLocaleString()}</span>
                <span>{quote.created_at.split("T")[0]}</span>
              </div>
            </div>
          ))}
          <div className="pt-2">
            <Pagination page={page} totalPages={totalPages} total={total} perPage={perPage} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Desktop table */}
      <Card className="card-industrial overflow-hidden hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("quote_number")}>
                <span className="inline-flex items-center">Quote #<SortIcon field="quote_number" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white">Order #</TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("customer_name")}>
                <span className="inline-flex items-center">Customer<SortIcon field="customer_name" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("items")}>
                <span className="inline-flex items-center">Items<SortIcon field="items" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("total")}>
                <span className="inline-flex items-center">Total<SortIcon field="total" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("status")}>
                <span className="inline-flex items-center">Status<SortIcon field="status" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleSort("created_at")}>
                <span className="inline-flex items-center">Date<SortIcon field="created_at" /></span>
              </TableHead>
              <TableHead className="bg-slate-900 text-white">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
            ) : quotations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No quotations found</p>
                </TableCell>
              </TableRow>
            ) : (
              sortedQuotations.map((quote) => (
                <TableRow key={quote.id} className="hover:bg-slate-50" data-testid={`quote-row-${quote.id}`}>
                  <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {quote.order_number
                      ? <span className="text-green-700 font-semibold">{quote.order_number}</span>
                      : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{quote.customer_name}</p>
                      <p className="text-xs text-slate-500">{quote.company_name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{quote.items.length}</TableCell>
                  <TableCell className="font-mono">₹{quote.total.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className={`badge-status ${statusColors[quote.status]}`}>
                      {statusLabels[quote.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{quote.created_at.split("T")[0]}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewDetail(quote)}
                      className="h-8 w-8 p-0"
                      data-testid={`view-quote-${quote.id}`}
                    >
                      <Eye size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="p-4">
          <Pagination page={page} totalPages={totalPages} total={total} perPage={perPage} onPageChange={setPage} />
        </div>
      </Card>

      {/* Quote Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) { setEditMode(false); setSelectedQuote(null); setDeliveryDate(""); } }}>
        <DialogContent className="w-full max-w-5xl h-[100dvh] md:h-auto md:max-h-[90vh] overflow-y-auto rounded-none p-0 md:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
              <FileText size={20} />
              {selectedQuote?.quote_number}
              {editMode && <Badge className="bg-orange-100 text-orange-800 border-orange-200 ml-2">Editing</Badge>}
            </DialogTitle>
          </DialogHeader>

          {detailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
          {!detailLoading && selectedQuote && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Customer</p>
                  <p className="font-medium mt-1">{selectedQuote.customer_name}</p>
                  <p className="text-sm text-slate-500">{selectedQuote.company_name}</p>
                </div>
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Status</p>
                  <Badge className={`badge-status mt-1 ${statusColors[selectedQuote.status]}`}>
                    {statusLabels[selectedQuote.status]}
                  </Badge>
                </div>
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
                  <p className="font-mono text-lg mt-1">₹{selectedQuote.total.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Freight</p>
                  <p className="font-mono text-lg mt-1">₹{(selectedQuote.freight_cost || 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Edit Mode: Items Table */}
              {editMode && (
                <div className="border-2 border-orange-300 bg-orange-50/30">
                  <div className="bg-orange-500 text-white px-4 py-2.5 flex items-center gap-2">
                    <Pencil size={16} />
                    <h3 className="text-sm font-semibold uppercase tracking-wider">Alter Order Items</h3>
                  </div>
                  <div className="p-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="bg-slate-800 text-white">Product</TableHead>
                          <TableHead className="bg-slate-800 text-white">Design File</TableHead>
                          <TableHead className="bg-slate-800 text-white text-center">Quantity</TableHead>
                          <TableHead className="bg-slate-800 text-white text-right">Current Total</TableHead>
                          <TableHead className="bg-slate-800 text-white text-center w-20">Remove</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editItems.map((item, i) => (
                          <TableRow key={i} className={item.remove ? "bg-red-50 opacity-50 line-through" : ""} data-testid={`edit-item-row-${i}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{item.product_name}</p>
                                <p className="text-xs text-slate-500 font-mono">{item.product_code}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{item.filename}</TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(e) => updateEditQty(i, e.target.value)}
                                disabled={item.remove}
                                className="w-20 mx-auto rounded-none font-mono text-center h-9"
                                data-testid={`edit-qty-${i}`}
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              ₹{item.item_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                variant={item.remove ? "default" : "ghost"}
                                onClick={() => toggleRemoveItem(i)}
                                className={`h-8 w-8 p-0 ${item.remove ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-500 hover:text-red-700 hover:bg-red-50"}`}
                                data-testid={`remove-item-btn-${i}`}
                              >
                                {item.remove ? <X size={14} /> : <Trash2 size={14} />}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {editItems.filter(i => !i.remove).length === 0 && (
                      <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                        <AlertTriangle size={14} /> At least one item must remain
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Item Details (view mode) */}
              {!editMode && selectedQuote.items.map((item, index) => (
                <OrderItemDetail key={index} item={item} index={index} />
              ))}

              {/* Cost Summary */}
              <div className="border border-slate-200 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-4 pb-2 border-b">Cost Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="font-mono">₹{selectedQuote.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Freight Cost</span>
                    <span className="font-mono">₹{selectedQuote.freight_cost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t text-lg font-bold">
                    <span>Total</span>
                    <span className="font-mono text-orange-500">₹{selectedQuote.total.toLocaleString()}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 pt-1">* GST will be Charged Extra as Applicable</p>
                </div>
              </div>

              {/* Notes */}
              {selectedQuote.customer_notes && (
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Customer Notes</p>
                  <p className="text-sm">{selectedQuote.customer_notes}</p>
                </div>
              )}

              {selectedQuote.admin_notes && !editMode && (
                <div className="bg-blue-50 border border-blue-200 p-4">
                  <p className="text-xs text-blue-500 uppercase tracking-wider mb-2">Admin Notes</p>
                  <p className="text-sm">{selectedQuote.admin_notes}</p>
                </div>
              )}

              {selectedQuote.expected_delivery_date && (
                <div className="bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Expected Delivery</p>
                  <p className="text-sm font-mono">{selectedQuote.expected_delivery_date}</p>
                </div>
              )}

              {(selectedQuote.delivery_schedule || []).length > 0 && (
                <div className="border border-slate-200">
                  <div className="bg-slate-900 text-white px-4 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wider">Desired Delivery Schedule</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <div className="grid grid-cols-[1fr_140px] gap-2 bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                      <span>Date</span>
                      <span>Quantity</span>
                    </div>
                    {selectedQuote.delivery_schedule.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_140px] gap-2 px-4 py-2 text-sm">
                        <span className="font-mono">{row.date || "—"}</span>
                        <span className="font-mono font-medium">{row.quantity} pcs</span>
                      </div>
                    ))}
                    <div className="grid grid-cols-[1fr_140px] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-600">
                      <span>Total</span>
                      <span className="font-mono">{selectedQuote.delivery_schedule.reduce((s, r) => s + (parseInt(r.quantity) || 0), 0)} pcs</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection Reason (visible to all when rejected) */}
              {selectedQuote.status === "rejected" && selectedQuote.rejection_reason && (
                <div className="bg-red-50 border border-red-200 p-4">
                  <p className="text-xs text-red-500 uppercase tracking-wider mb-2">Rejection Reason</p>
                  <p className="text-sm text-red-800">{selectedQuote.rejection_reason}</p>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t space-y-4">

                {/* ADMIN Actions on Draft */}
                {isSalesAdmin && selectedQuote.status === "draft" && (
                  <div className="space-y-4">
                    {/* Edit Mode Toggle */}
                    {!editMode ? (
                      <Button
                        onClick={enterEditMode}
                        variant="outline"
                        className="w-full rounded-none border-orange-300 text-orange-700 hover:bg-orange-50"
                        data-testid="alter-order-btn"
                      >
                        <Pencil size={16} className="mr-2" />
                        Alter Order (Qty / Remove Items)
                      </Button>
                    ) : (
                      <div className="flex gap-3">
                        <Button
                          onClick={saveAlterations}
                          disabled={saving || editItems.filter(i => !i.remove).length === 0}
                          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-none"
                          data-testid="save-alterations-btn"
                        >
                          <Save size={16} className="mr-2" />
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button
                          onClick={() => setEditMode(false)}
                          variant="outline"
                          className="rounded-none"
                          data-testid="cancel-edit-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Freight + Delivery Date + Notes + Confirm */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Freight Cost (₹)</Label>
                        <Input
                          type="number"
                          value={freightCost}
                          onChange={(e) => setFreightCost(parseFloat(e.target.value) || 0)}
                          className="rounded-none font-mono mt-2"
                          data-testid="freight-cost-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Delivery Date</Label>
                        <Input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          className="rounded-none font-mono mt-2"
                          data-testid="delivery-date-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Admin Notes</Label>
                        <Textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Internal notes or message to customer..."
                          className="rounded-none mt-2"
                          rows={2}
                          data-testid="admin-notes-input"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={sendConfirmationMail}
                        disabled={confirming || editMode}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-none"
                        data-testid="send-confirmation-button"
                      >
                        <Mail size={18} className="mr-2" />
                        {confirming ? "Sending..." : "Send Confirmation to Customer"}
                      </Button>
                      <Button
                        onClick={() => { setShowRejectDialog(true); setRejectionReason(""); }}
                        disabled={confirming || editMode}
                        variant="outline"
                        className="rounded-none text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="admin-reject-btn"
                      >
                        <Ban size={16} className="mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}

                {/* Customer actions on pending_approval */}
                {isCustomer() && selectedQuote.status === "pending_approval" && (
                  <div className="space-y-3">
                    {selectedQuote.admin_notes && (
                      <div className="bg-blue-50 border border-blue-200 p-4">
                        <p className="text-xs text-blue-500 uppercase tracking-wider mb-2">Message from Admin</p>
                        <p className="text-sm">{selectedQuote.admin_notes}</p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button onClick={approveQuotation} disabled={confirming} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-none" data-testid="approve-quote-button">
                        <Check size={18} className="mr-2" /> {confirming ? "Processing..." : "Approve"}
                      </Button>
                      <Button onClick={rejectQuotation} disabled={confirming} variant="outline" className="flex-1 rounded-none text-red-600 border-red-200" data-testid="reject-quote-button">
                        <X size={18} className="mr-2" /> Reject
                      </Button>
                    </div>
                  </div>
                )}

                {selectedQuote.status === "converted" && (
                  <div className="bg-green-50 border border-green-200 p-4 text-center">
                    <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-heading uppercase tracking-wider text-green-800">Converted to Order</p>
                    {selectedQuote.order_number && (
                      <p className="text-sm font-mono font-bold text-green-700 mt-1">Order No: {selectedQuote.order_number}</p>
                    )}
                  </div>
                )}

                {selectedQuote.status === "rejected" && !selectedQuote.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 p-4 text-center">
                    <X className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="font-heading uppercase tracking-wider text-red-800">Quotation Rejected</p>
                  </div>
                )}

                {selectedQuote.status === "pending_approval" && !isCustomer() && (
                  <div className="bg-blue-50 border border-blue-200 p-4 text-center">
                    <p className="text-sm text-blue-800">Waiting for customer approval</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-wider flex items-center gap-2 text-red-700">
              <Ban size={20} />
              Reject Quotation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              This will reject <strong>{selectedQuote?.quote_number}</strong> for customer <strong>{selectedQuote?.customer_name}</strong>. Please provide a reason.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider">Rejection Reason *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Design file is unclear, quantity too low for production run, product discontinued..."
                className="rounded-none mt-2"
                rows={3}
                data-testid="rejection-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={adminRejectQuotation}
                disabled={rejecting || !rejectionReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-none"
                data-testid="confirm-reject-btn"
              >
                {rejecting ? "Rejecting..." : "Confirm Rejection"}
              </Button>
              <Button
                onClick={() => setShowRejectDialog(false)}
                variant="outline"
                className="flex-1 rounded-none"
                data-testid="cancel-reject-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
